import React, { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import queryString from 'query-string';
import { useLocation } from '@reach/router';
import { useIntl } from 'gatsby-plugin-intl';
import useMedia from 'use-media';
import Img from 'gatsby-image';
import { HiStar, HiOutlineCheckCircle, HiOutlineCreditCard, HiOutlineGlobe } from 'react-icons/hi';

import LanguageContext from '../../contexts/LanguageContext';
import { PRICES } from '../../components/Sections/DeviceProducts';
import { useShopify } from '../../contexts/ShopifyContext';
import { useSpinner } from '../../contexts/SpinnerContext';

const SquarePattern = styled.div`
  background: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAF0lEQVQImWNgwAe0nFP/YwjAMH6V6AAA1rQJFif/QB4AAAAASUVORK5CYII=)
    repeat;
  width: 0;
  height: 0;
  left: -104px;
  bottom: -104px;
  z-index: -1;
  @media (min-width: 768px) {
    width: 200px;
    height: 200px;
    left: -50px;
    bottom: -50px;
  }
  @media (min-width: 1024px) {
    width: 400px;
    height: 400px;
    left: -104px;
    bottom: -104px;
  }
`;

const Description = styled.div`
  ul {
    list-style: circle;
    padding-left: 18px;
  }
  li {
    margin-bottom: 6px;
  }
`;

export const ProductDetails = ({ product, liveProduct }) => {
  const { toggleSpinner } = useSpinner();
  const { addVariantToCart } = useShopify();
  const { search } = useLocation();
  const { currency } = useContext(LanguageContext);
  const { variants, handle, images } = product;
  const [currentVariant, setCurrentVariant] = useState(variants[0]);
  const [imageIndex, setImageIndex] = useState(0);
  const { formatMessage, locale } = useIntl();
  const isTablet = useMedia({ minWidth: '768px' });

  const price = PRICES[handle][currency].price;
  const compareAtPrice = PRICES[handle][currency].compareAtPrice;

  let livePrice, liveCompareAtPrice, liveVariant;
  if (liveProduct) {
    liveVariant = liveProduct.variants.edges.find(({ node }) => node.title === currentVariant.title);
    if (liveVariant) {
      livePrice = liveVariant.node.price;
      liveCompareAtPrice = liveVariant.node.compareAtPrice;
    }
  }
  console.log(livePrice, liveCompareAtPrice, liveVariant);

  const selectVariant = (id) => {
    const variant = variants.find(({ shopifyId }) => shopifyId === id);
    setCurrentVariant(variant);
  };

  const handleAddToCart = async () => {
    toggleSpinner(true);
    await addVariantToCart(liveVariant ? liveVariant.node.id : currentVariant.shopifyId, 1);
    toggleSpinner(false);
  };

  useEffect(() => {
    if (search) {
      const params = queryString.parse(search);
      console.log(params);
      params.variant && selectVariant(params.variant);
    }
  }, []);

  return (
    <section className="relative w-full xl:container px-4 sm:px-8 mx-auto pt-24 md:pb-48 lg:pt-64 lg:pb-16 flex flex-col items-center md:items-start md:flex-row md:justify-between">
      <div className="w-full sm:w-2/3 md:w-2/5 lg:w-1/2 md:pr-4 lg:pr-8 mb-4">
        <div className="relative border-8 border-blue-900 bg-orange-100 w-full">
          <SquarePattern className="absolute" />
          <Img fluid={images[imageIndex].localFile.childImageSharp.fluid} />
          <div className="absolute bottom-4 flex justify-center px-2 w-full z-10">
            <div className="flex justify-center items-center bg-white py-1 px-2 border border-blue-900 rounded-full">
              {(images || []).map((_, index) => (
                <div
                  key={`image-${index}`}
                  className={`w-3 h-3 rounded-full cursor-pointer mx-1 transition ${
                    imageIndex === index ? 'bg-blue-900' : 'border border-blue-900'
                  }`}
                  onClick={() => setImageIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full md:w-3/5 lg:w-1/2 md:pl-4 lg:pl-8">
        <h1 className="font-heading font-bold text-2xl sm:w-96 mb-4">
          {formatMessage({ id: `products.${handle}.title` })}
        </h1>
        <div className="flex items-center mb-4">
          <HiStar size={isTablet ? 40 : 36} className="" />
          <HiStar size={isTablet ? 40 : 36} className="" />
          <HiStar size={isTablet ? 40 : 36} className="" />
          <HiStar size={isTablet ? 40 : 36} className="" />
          <HiStar size={isTablet ? 40 : 36} className="mr-5" />
          {/* <HiOutlineStar size={isTablet ? 40 : 36} className="mr-5" /> */}
          <span>256 Reviews</span>
        </div>
        <div className="font-heading mb-4">
          <span className="inline-block pr-3 font-bold text-xl sm:text-4xl text-blue-600">
            {Intl.NumberFormat(locale, { style: 'currency', currency }).format(livePrice || price)}
          </span>
          <span className="line-through sm:text-2xl text-gray-600">
            {Intl.NumberFormat(locale, { style: 'currency', currency }).format(liveCompareAtPrice || compareAtPrice)}
          </span>
        </div>
        <Description
          className="mb-4 list-disc"
          dangerouslySetInnerHTML={{ __html: formatMessage({ id: 'productpage.description.iphone' }) }}
        />
        <div className="flex flex-wrap mb-4">
          {variants.map(({ id, title, shopifyId }) => (
            <div
              key={id}
              onClick={() => selectVariant(shopifyId)}
              className={`border border-blue-900 mr-2 p-2 rounded-sm mb-2 text-xs bg-white hover:bg-blue-100 cursor-pointer ${
                shopifyId === currentVariant.shopifyId && 'bg-blue-100'
              }`}>
              {title}
            </div>
          ))}
        </div>
        <button
          onClick={handleAddToCart}
          className="text-lg md:text-xl bg-blue-900 hover:bg-blue-800 w-full rounded py-3 lg:py-4 text-white font-heading mb-4">
          In den Warenkorb
        </button>
        <div className="flex items-center font-medium mb-4">
          <HiOutlineCheckCircle size={24} className="mr-2" />
          {formatMessage({ id: 'promises.guarantee.title' })}
        </div>
        <div className="flex items-center font-medium mb-4">
          <HiOutlineCreditCard size={24} className="mr-2" />
          {formatMessage({ id: 'promises.payment.title' })}
        </div>
        <div className="flex items-center font-medium mb-4">
          <HiOutlineGlobe size={24} className="mr-2" />
          {formatMessage({ id: 'promises.delivery.title' })}
        </div>
      </div>
    </section>
  );
};
